import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useForm } from "react-hook-form";
import Button from "../../../components/Buttons/Button";
import TextField from "../../../components/Form/TextField";
import ContentApprovalCommentList from "../../../components/ContentApproval/ContentApprovalCommentList";
import ApprovalCurrentReviewerBanner from "../../../components/ContentApproval/ApprovalCurrentReviewerBanner";
import { contentApprovalSidebarSx } from "../../../components/ContentApproval/contentApprovalLayout";
import { useTranslationApp } from "../../../lib/i18next";
import {
  CAMPAIGN_APPROVAL_COMMENT_BUTTON,
  CONTENT_APPROVAL_ALL_GOOD_QUESTION,
  CONTENT_APPROVAL_COMMENT_PLACEHOLDER,
  CONTENT_APPROVAL_REVIEWER_COMMENTS_EMPTY,
  CONTENT_APPROVAL_APPROVE_CONTENT_BUTTON,
  CONTENT_APPROVAL_SIDEBAR_TITLE,
  CONTENT_APPROVAL_STATUS_SECTION_LABEL,
  FIELD_REQUIRED,
} from "../../../i18n/keysTranslations";
import { INPUT_COLOR_BORDER } from "../../../utils/colors";
import {
  filterActiveContentApprovalComments,
  formatContentApprovalSentDate,
} from "../../../components/ContentApproval/contentApprovalUtils";
import ContentApprovalStatusChip from "../../../components/ContentApproval/ContentApprovalStatusChip";
import { hasMultipleApprovalReviewers } from "../../../components/ContentApproval/approvalReviewersUtils";

const ContentApprovalSidebar = ({
  comments = [],
  selectedContentID = "",
  currentDraftID = "",
  currentDraftIDsByContentID = {},
  commentsLocked = false,
  isSubmittingComment = false,
  isSubmittingDecision = false,
  sentAt = null,
  approvalStatus = null,
  reviewers = [],
  currentReviewer = null,
  onSubmitComment = () => {},
  onSubmitApprove = () => {},
}) => {
  const { t } = useTranslationApp();
  const { control, handleSubmit, reset, watch, formState } = useForm({
    defaultValues: {
      body: "",
    },
  });

  const commentBody = watch("body") || "";
  const sentDateFormatted = formatContentApprovalSentDate(sentAt);

  const visibleComments = filterActiveContentApprovalComments(comments, {
    contentID: selectedContentID,
    currentDraftID,
    currentDraftIDsByContentID,
    types: ["general"],
  });
  const canApprove = approvalStatus === "pending";
  const shouldShowReviewerBanner =
    Boolean(currentReviewer) && hasMultipleApprovalReviewers(reviewers);

  const onHandleSubmitComment = handleSubmit(async ({ body }) => {
    const normalizedBody = body?.trim?.() || "";
    if (!normalizedBody) {
      return;
    }

    const response = await onSubmitComment({ body: normalizedBody });
    if (response?.ok) {
      reset({ body: "" });
    }
  });

  return (
    <Box sx={contentApprovalSidebarSx}>
      {(sentDateFormatted || approvalStatus || shouldShowReviewerBanner) && (
        <Box
          sx={{
            p: 2.5,
            borderBottom: `1px solid ${INPUT_COLOR_BORDER}`,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            flexShrink: 0,
          }}
        >
          <ApprovalCurrentReviewerBanner
            currentReviewer={currentReviewer}
            reviewers={reviewers}
          />

          {approvalStatus ? (
            <Box>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 0.75,
                  textTransform: "uppercase",
                  fontWeight: 700,
                  letterSpacing: "0.5px",
                  color: "text.secondary",
                  fontSize: "0.65rem",
                }}
              >
                {t(CONTENT_APPROVAL_STATUS_SECTION_LABEL)}
              </Typography>
              <ContentApprovalStatusChip status={approvalStatus} />
            </Box>
          ) : null}
        </Box>
      )}

      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Typography variant="body1" sx={{ fontWeight: 700, flex: 1 }}>
          {t(CONTENT_APPROVAL_SIDEBAR_TITLE)}
        </Typography>
        {visibleComments.length > 0 && (
          <Box
            sx={{
              width: 22,
              height: 22,
              borderRadius: "50%",
              bgcolor: "#111",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: "#fff", fontWeight: 700, fontSize: "0.65rem" }}
            >
              {visibleComments.length}
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          px: 2.5,
          py: 2,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <ContentApprovalCommentList
          comments={visibleComments}
          showCreativeLabel={false}
          centeredEmpty
          emptyText={t(CONTENT_APPROVAL_REVIEWER_COMMENTS_EMPTY)}
        />
      </Box>

      {!commentsLocked ? (
        <Box sx={{ flexShrink: 0 }}>
          <Box
            component="form"
            onSubmit={onHandleSubmitComment}
            sx={{
              px: 2.5,
              pt: 2,
              pb: 2.5,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              borderTop: `1px solid ${INPUT_COLOR_BORDER}`,
            }}
          >
            <TextField
              control={control}
              name="body"
              label=""
              placeholder={t(CONTENT_APPROVAL_COMMENT_PLACEHOLDER)}
              fullWidth
              multiline
              minRows={4}
              variant="filled"
              disabled={isSubmittingComment || isSubmittingDecision}
              rules={{
                required: {
                  value: true,
                  message: t(FIELD_REQUIRED),
                },
                validate: (value) => {
                  return value?.trim?.() ? true : t(FIELD_REQUIRED);
                },
              }}
            />
            <Button
              type="submit"
              color="secondary"
              fullWidth
              disabled={!commentBody.trim() || isSubmittingComment}
              loading={isSubmittingComment || formState.isSubmitting}
            >
              {t(CAMPAIGN_APPROVAL_COMMENT_BUTTON)}
            </Button>
          </Box>

          <Box
            sx={{
              px: 2.5,
              pt: 2.5,
              pb: 3,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              borderTop: `1px solid ${INPUT_COLOR_BORDER}`,
            }}
          >
            <Typography variant="body2" color="text.secondary" align="center">
              {t(CONTENT_APPROVAL_ALL_GOOD_QUESTION)}
            </Typography>
            <Button
              type="button"
              fullWidth
              disabled={
                !canApprove || isSubmittingDecision || isSubmittingComment
              }
              loading={isSubmittingDecision}
              onClick={onSubmitApprove}
            >
              {t(CONTENT_APPROVAL_APPROVE_CONTENT_BUTTON)}
            </Button>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

export default ContentApprovalSidebar;
